import { defineStore } from "pinia";
import { useAutoSuggestStore } from "./auto-suggest";
import { searchPrepareParamsForFilterProducts } from '@/utils/api-drive-params'
import magasinRechercheApiDrive from "@/services/http/api-drive/magasin/recherche";

import coraproUtils from "@/utils/corapro-code";

export const useProductSearchStore = defineStore({
  id: 'product-search',
  state: () => {
    return {
      attributes: null,
      productsList: [],
      pagination: {},
      _filters: [],
      _appliedFilters: [],
      productsListMode: "Grid",
      selectedProduct: {},
      showMoreClicked: false,
      showPreviousClicked: false,
      firstPageIndex: 1,
      lastPageIndex: 1,
      productsCount: 0,
      isLoading: false,
      isLoadingInfinite: false,
      errorStatus: '',
      seoHead: {},
      redirection: null,
      filterVisible: false,
      sections: [],
      fromWordSuggestion: false
    }
  },
  actions: {
    searchResultsFetched(payload) {
      this.attributes = payload.data.attributes;
      if (payload.meta.redirection.product_list) {
        this.redirection = payload.meta.redirection.product_list
        return;
      } else {
        this.redirection = payload.meta.redirection.article ? payload.meta.redirection.article : null
      }
      this.pagination = payload.data.attributes.product_list !== null ? payload.data.attributes.product_list.meta.pagination : [];
      if (this.showMoreClicked) {
        this.productsList = [...this.productsList, ...payload.data.attributes.product_list.data]
        this.lastPageIndex += 1;
        this.productsCount = this.pagination.nb_items + ((this.pagination.current_page - 1) * this.pagination.per_page);
        this.showMoreClicked = false
        if (payload.data.attributes.product_list) {
          this.sections.push({ page: this.pagination.current_page, products: payload.data.attributes.product_list.data })
        }
      } else if (this.showPreviousClicked) {
        this.productsList = [...payload.data.attributes.product_list.data, ...this.productsList]
        this.firstPageIndex -= 1;
        this.showPreviousClicked = false
        if (payload.data.attributes.product_list) {
          this.sections.unshift({ page: this.pagination.current_page, products: payload.data.attributes.product_list.data })
        }
      } else {
        this.productsList = payload.data.attributes.product_list.data;
        this.firstPageIndex = this.pagination.current_page
        this.lastPageIndex = this.pagination.current_page
        this.productsCount = this.pagination.nb_items + ((this.pagination.current_page - 1) * this.pagination.per_page);
        if (payload.data.attributes.product_list) {
          this.sections = [{ page: this.pagination.current_page, products: payload.data.attributes.product_list.data }]
        }
      }
      this.pagination.nb_items = this.productsCount;
      this._filters = payload.data.attributes.product_list.meta.facettes;
      const filtersCoraProIndex = this._filters.findIndex((element) => element.code === coraproUtils.SEARCH_SPARKOW_CORA_PRO_CODE);
      if (filtersCoraProIndex >= 0) { this._filters.splice(filtersCoraProIndex, 1); }
      this._appliedFilters = payload.data.attributes.product_list.meta.facettes_actives
      const appliedFiltersCoraProIndex = this._appliedFilters.findIndex((element) => element.code === coraproUtils.SEARCH_SPARKOW_CORA_PRO_CODE);
      if (appliedFiltersCoraProIndex >= 0) { this._appliedFilters.splice(appliedFiltersCoraProIndex, 1); }
      this.seoHead = payload.meta.head
    },
    similarProductsSet(payload) {
      this.similarProducts = payload.data.attributes.product_list.data;
      this.selectedProduct = payload.data.selectedProduct;
    },
    setShowMoreClicked(value) {
      this.showMoreClicked = value;
    },
    setShowPreviousClicked(value) {
      this.showPreviousClicked = value;
    },
    setFromWordSuggestion(value) {
      this.fromWordSuggestion = value;
    },
    updateFilterVisible(value) {
      this.filterVisible = value;
    },
    updateLoadingStatus(value, infinite = false) {
      if (!value) {
        this.isLoadingInfinite = false;
        this.isLoading = false;
        return;
      }
      if (infinite) {
        this.isLoadingInfinite = value;
      } else {
        this.isLoading = value;
      }
    },
    setErrorStatus(err) {
      this.errorStatus = err.response ? err.response.status : 504;
    },
    resetStore() {
      this.productsList = [];
      this.pagination = {};
      this._filters = [];
      this._appliedFilters = [];
      this.productsListMode = "Grid";
      this.selectedProduct = {};
      this.showMoreClicked = false;
      this.showPreviousClicked = false;
      this.firstPageIndex = 1;
      this.lastPageIndex = 1;
      this.productsCount = 0;
      this.isLoading = false;
      this.isLoadingInfinite = false;
      this.errorStatus = '';
      this.seoHead = {};
      this.redirection = null;
      this.sections = [];
    },
    async fetchSearchResults(params) {
      this.updateLoadingStatus(true);
      if (!params.isPro) {
        params.filterList = [{ code: coraproUtils.SEARCH_SPARKOW_CORA_PRO_CODE, value: coraproUtils.SEARCH_SPARKOW_CORA_PRO_STANDARD_VALUE }];
      }
      try {
        const response = await this.$apiDrive.use(magasinRechercheApiDrive).search(params);
        this.searchResultsFetched(response);
        return Promise.resolve(response);
      } catch (err) {
        this.setErrorStatus(err);
        this.updateLoadingStatus(false);
        return Promise.reject(err);
      }
    },
    async fetchFiltredSearchResults(params) {
      const autoSuggestStore = useAutoSuggestStore();

      this.updateLoadingStatus(true, this.showMoreClicked);
      if (!params.isPro) {
        const coraproFilter = { code: coraproUtils.SEARCH_SPARKOW_CORA_PRO_CODE, value: coraproUtils.SEARCH_SPARKOW_CORA_PRO_STANDARD_VALUE };
        if (params.filterList) {
          params.filterList.push(coraproFilter);
        } else {
          params.filterList = [coraproFilter];
        }
      }

      try {
        const response = await this.$apiDrive.use(magasinRechercheApiDrive).search(searchPrepareParamsForFilterProducts(params));
        autoSuggestStore.setHistoryWords(response.meta.historique_recherche);
        this.searchResultsFetched(response);
        return Promise.resolve(response);
      } catch (err) {
        this.setErrorStatus(err);
        return Promise.reject(err);
      } finally {
        this.updateLoadingStatus(false);
      }
    }
  },
  getters: {
    filters: (state) => {
      if (state._filters && state.$config.public.HIDDEN_FILTERS_RECHERCHE) {
        return state._filters.filter(f => {
          return !state.$config.public.HIDDEN_FILTERS_RECHERCHE.includes(f.code)
        });
      }
      return state._filters;
    },
    appliedFilters: (state) => {
      if (state._appliedFilters && state.$config.HIDDEN_FILTERS_RECHERCHE) {
        return state._appliedFilters.filter(f => {
          return !state.$config.public.HIDDEN_FILTERS_RECHERCHE.includes(f.code)
        });
      }
      return state._appliedFilters;
    },
    title: (state) => {
      return state.attributes && state.attributes.title;
    },
    description: (state) => {
      return state.attributes && state.attributes.description;
    },
    sortCriteria: (state) => {
      return state.attributes && state.attributes.product_list.meta.tri;
    },
    pushZones: (state) => {
      return state.attributes && state.attributes.push_zones;
    },
    keyWord: (state) => {
      return state.attributes && state.attributes.keywords;
    },
    childrenCategories: (state) => {
      return state.attributes && state.attributes.categories ? state.attributes.categories.data : [];

    },
  }
})